import { defineStore } from 'pinia'
import { authApi } from '@/services/api'
import type { LoginRequest, User, UserRequest, UserUpdateRequest, PasswordChangeRequest } from '@/types'

const TOKEN_KEY = 'seatisfy_token'
const USER_KEY = 'seatisfy_user'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null as string | null,
    user: null as User | null,
    isLoading: false
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.token),
    isAdmin: (state) => state.user?.role === 'ADMIN'
  },
  actions: {
    async initialize() {
      if (typeof window === 'undefined') return

      const storedToken = localStorage.getItem(TOKEN_KEY)
      if (storedToken) {
        this.token = storedToken
      }

      const storedUser = localStorage.getItem(USER_KEY)
      if (storedUser) {
        try {
          this.user = JSON.parse(storedUser) as User
        } catch {
          this.user = null
        }
      }

      if (this.token && !this.user) {
        try {
          const response = await authApi.getMe()
          this.user = response.data as User
          this.persistUser()
        } catch (error) {
          this.logout()
          throw error
        }
      }
    },

    async login(credentials: LoginRequest) {
      this.isLoading = true
      try {
        const response = await authApi.login(credentials)
        const payload = response.data as { token: string; user: User }
        this.setSession(payload.token, payload.user)
        return true
      } finally {
        this.isLoading = false
      }
    },

    async register(userData: UserRequest) {
      this.isLoading = true
      try {
        await authApi.register(userData)
        return true
      } finally {
        this.isLoading = false
      }
    },

    async findId(name: string, phone: string) {
      this.isLoading = true
      try {
        const response = await authApi.findId(name, phone)
        return response.data as string
      } finally {
        this.isLoading = false
      }
    },

    async resetPassword(email: string) {
      this.isLoading = true
      try {
        await authApi.resetPassword(email)
        return true
      } finally {
        this.isLoading = false
      }
    },

    async updateProfile(userData: UserUpdateRequest) {
      if (!this.user) return false
      this.isLoading = true
      try {
        const response = await authApi.updateProfile(userData)
        this.user = response.data as User
        this.persistUser()
        return true
      } finally {
        this.isLoading = false
      }
    },

    async changePassword(payload: PasswordChangeRequest) {
      if (!this.user) return false
      this.isLoading = true
      try {
        await authApi.changePassword(payload)
        return true
      } finally {
        this.isLoading = false
      }
    },

    logout() {
      this.token = null
      this.user = null
      if (typeof window !== 'undefined') {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
      }
    },

    setSession(token: string, user: User) {
      this.token = token
      this.user = user
      if (typeof window !== 'undefined') {
        localStorage.setItem(TOKEN_KEY, token)
        this.persistUser()
      }
    },

    persistUser() {
      if (typeof window === 'undefined') return
      if (this.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(this.user))
      } else {
        localStorage.removeItem(USER_KEY)
      }
    }
  }
})
